import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification } from 'antd'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { FloorplanForm } from '@/components/form/FloorplanForm'
import type { FloorplanFormValues } from '@/components/form/FloorplanForm'
import {
  createFloorplan,
  updateFloorplan,
  getFloorplanById,
} from '@/api/floorplans.api'
import { uploadMultipartFile } from '@/api/file.api'
import type { Floorplan } from '@/types/api'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface FloorplanFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  floorplan: Floorplan | null
  onClose: () => void
}

export function FloorplanFormDrawer({
  isOpen,
  mode,
  floorplan,
  onClose,
}: FloorplanFormDrawerProps) {
  const [form] = Form.useForm<FloorplanFormValues>()
  const queryClient = useQueryClient()
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const { data: detailData, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['floorplan-detail', floorplan?.id],
    queryFn: () => getFloorplanById(floorplan!.id),
    enabled: isOpen && mode === 'update' && !!floorplan?.id,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (!isOpen) {
      form.resetFields()
      return
    }

    if (mode === 'create') {
      form.resetFields()
      return
    }

    if (detailData) {
      form.setFieldsValue({
        id: detailData.id,
        code: detailData.code,
        name: detailData.name,
        unitId: detailData.unitId,
        unitName: detailData.unitName,
        width: detailData.width,
        height: detailData.height,
        description: detailData.description,
        imageFile: detailData.fileInfo?.fileUrl
          ? [
            {
              uid: detailData.fileInfo.id || '-1',
              name: detailData.fileInfo.originFileName || 'image.png',
              url: detailData.fileInfo.fileUrl,
            },
          ]
          : [],
      })
    }
  }, [isOpen, mode, detailData, form])

  const createMutation = useMutation({
    mutationFn: createFloorplan,
    onSuccess: (response) => {
      notification.success({
        title: response.responseMessage || 'Thêm mới sơ đồ thành công!',
      })
      queryClient.invalidateQueries({ queryKey: ['floorplans'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: any) => {
      if (!floorplan?.id) throw new Error('Missing floorplan ID')
      return updateFloorplan(floorplan.id, values)
    },
    onSuccess: (response) => {
      notification.success({
        title: response.responseMessage || 'Cập nhật sơ đồ thành công!',
      })
      queryClient.invalidateQueries({ queryKey: ['floorplans'] })
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isDrawerDisabled = isSubmitting || isUploadingImage || isLoadingDetail

  const handleSubmit = async (values: FloorplanFormValues) => {
    try {
      let targetImageId =
        values.imageFile?.[0]?.url || values.imageFile?.[0]?.name
      const newFileToUpload = values.imageFile?.[0]?.originFileObj

      if (newFileToUpload) {
        setIsUploadingImage(true)
        targetImageId = await uploadMultipartFile(newFileToUpload, 'diagrams')
        setIsUploadingImage(false)
      } else if (mode === 'update' && detailData?.fileInfo) {
        targetImageId = detailData.fileInfo.id
      }

      const payload = {
        name: values.name,
        unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
        image: targetImageId,
        width: values.width ? Number(values.width) : undefined,
        height: values.height ? Number(values.height) : undefined,
        description: values.description,
      }

      if (mode === 'create') {
        createMutation.mutate(payload)
      } else {
        updateMutation.mutate(payload)
      }
    } catch (error) {
      setIsUploadingImage(false)
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI SƠ ĐỒ' : 'CẬP NHẬT SƠ ĐỒ'}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isUploadingImage
              ? 'Đang tải ảnh...'
              : isSubmitting
                ? 'Đang lưu...'
                : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <div className="min-h-[300px]">
        {mode === 'update' && isLoadingDetail ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            Đang tải dữ liệu chi tiết...
          </div>
        ) : (
          <FloorplanForm
            form={form}
            mode={mode}
            showUnitColumn={showUnitColumn}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </Drawer>
  )
}
